<template>
  <div>
    <div class="mb-3">
      <div class="row g-2 align-items-end">
        <div class="col-md-4">
          <label class="form-label small fw-bold">Nombre de Archivo</label>
          <input v-model="filtros.nombre" type="text" class="form-control form-control-sm" placeholder="Buscar archivo..." />
        </div>
        <div class="col-md-3">
          <label class="form-label small fw-bold">Tipo</label>
          <select v-model="filtros.tipo" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option v-for="t in tiposUnicos" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label small fw-bold">ID Planilla (CSV)</label>
          <input v-model="filtros.csvId" type="text" class="form-control form-control-sm" placeholder="Filtrar ID..." />
        </div>
        <div class="col-md-2 d-flex gap-2">
          <button @click="limpiarFiltros" class="btn btn-outline-secondary btn-sm flex-fill">
            Limpiar
          </button>
          <button @click="$emit('refrescar')" class="btn btn-outline-primary btn-sm flex-fill" :disabled="loading">
            Refrescar
          </button>
        </div>
      </div>
    </div>

    <div class="d-flex gap-3 mb-3 flex-wrap" v-if="!loading && datosFiltrados.length">
      <div class="border rounded px-3 py-2 text-center bg-light">
        <div class="fw-bold fs-5">{{ datosFiltrados.length }}</div>
        <div class="text-muted small">Archivos encontrados</div>
      </div>
      <div class="border rounded px-3 py-2 text-center bg-light">
        <div class="fw-bold fs-5 text-primary">{{ pesoTotal }}</div>
        <div class="text-muted small">Peso Total</div>
      </div>
      <div class="border rounded px-3 py-2 text-center bg-light ms-auto">
        <button @click="exportarCSV" class="btn btn-success btn-sm">📥 Exportar Lista</button>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger py-2 small">⚠️ {{ error }}</div>

    <div v-if="loading" class="text-center my-4">
      <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
      <span class="ms-2 small text-muted">Consultando archivos sin planilla...</span>
    </div>

    <div v-if="!loading" class="table-responsive">
      <table class="table table-hover table-bordered table-sm align-middle" style="font-size: 0.9rem;">
        <thead class="table-dark">
          <tr>
            <th @click="ordenarPor('csvIdPlanilla')" class="th-sort">ID Planilla {{ iconOrden('csvIdPlanilla') }}</th>
            <th @click="ordenarPor('nombre')"        class="th-sort">Nombre Archivo {{ iconOrden('nombre') }}</th>
            <th @click="ordenarPor('tipo')"          class="th-sort text-center">Tipo {{ iconOrden('tipo') }}</th>
            <th @click="ordenarPor('tamano')"        class="th-sort text-end">Tamaño {{ iconOrden('tamano') }}</th>
            <th class="text-center">Ruta</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in datosPaginados" :key="item.csvIdPlanilla + item.nombre">
            <td class="fw-bold text-primary">{{ item.csvIdPlanilla }}</td>
            <td>{{ item.nombre }}</td>
            <td class="text-center"><span class="badge bg-secondary">{{ item.tipo }}</span></td>
            <td class="text-end text-muted">{{ formatBytes(item.tamano) }}</td>
            <td class="text-center">
               <small class="text-truncate d-inline-block" style="max-width: 200px;" :title="item.ruta">
                 {{ item.ruta }}
               </small>
            </td>
          </tr>
          <tr v-if="datosFiltrados.length === 0">
            <td colspan="5" class="text-center text-muted py-4">No se encontraron archivos pendientes de planilla para este trámite.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="datosFiltrados.length > 0" class="d-flex justify-content-center mt-3 gap-2 align-items-center">
      <button class="btn btn-outline-secondary btn-sm" :disabled="paginaActual === 1" @click="paginaActual = 1">«</button>
      <button class="btn btn-outline-secondary btn-sm" :disabled="paginaActual === 1" @click="paginaActual--">‹</button>
      <span class="small mx-2">Pág. {{ paginaActual }} de {{ totalPaginas }}</span>
      <button class="btn btn-outline-secondary btn-sm" :disabled="paginaActual === totalPaginas" @click="paginaActual++">›</button>
      <button class="btn btn-outline-secondary btn-sm" :disabled="paginaActual === totalPaginas" @click="paginaActual = totalPaginas">»</button>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'

export default {
  name: 'PlanillaArchivo',
  props: {
    data:    { type: Array,   default: () => [] },
    loading: { type: Boolean, default: false },
    error:   { type: String,  default: null },
  },
  emits: ['refrescar'],
  setup(props) {
    const paginaActual = ref(1)
    const porPagina    = ref(15)
    const orden        = ref({ campo: 'csvIdPlanilla', asc: true })

    const filtros = ref({
      nombre: '',
      tipo:   '',
      csvId:  ''
    })

    // Tipos únicos para el select
    const tiposUnicos = computed(() => [...new Set(props.data.map(i => i.tipo))].sort())

    // ── Filtrado ──────────────────────────────────────────────────────────
    const datosFiltrados = computed(() =>
      props.data.filter(item => {
        const f = filtros.value
        if (f.nombre && !item.nombre.toLowerCase().includes(f.nombre.toLowerCase())) return false
        if (f.tipo && item.tipo !== f.tipo) return false
        if (f.csvId && !String(item.csvIdPlanilla).includes(f.csvId)) return false
        return true
      })
    )

    // ── Ordenamiento ──────────────────────────────────────────────────────
    const datosOrdenados = computed(() => {
      const campo = orden.value.campo
      return [...datosFiltrados.value].sort((a, b) => {
        const va = a[campo] ?? ''
        const vb = b[campo] ?? ''
        const cmp = (typeof va === 'number') ? va - vb : String(va).localeCompare(String(vb))
        return orden.value.asc ? cmp : -cmp
      })
    })

    // ── Paginación ────────────────────────────────────────────────────────
    const totalPaginas = computed(() => Math.max(1, Math.ceil(datosOrdenados.value.length / porPagina.value)))
    const datosPaginados = computed(() => {
      const ini = (paginaActual.value - 1) * porPagina.value
      return datosOrdenados.value.slice(ini, ini + porPagina.value)
    })

    // ── Resumen ───────────────────────────────────────────────────────────
    const pesoTotal = computed(() => {
      const total = datosFiltrados.value.reduce((a, r) => a + Number(r.tamano || 0), 0)
      return formatBytes(total)
    })

    watch([filtros, orden], () => { paginaActual.value = 1 }, { deep: true })

    // ── Helpers ───────────────────────────────────────────────────────────
    const ordenarPor = (campo) => {
      orden.value = orden.value.campo === campo ? { campo, asc: !orden.value.asc } : { campo, asc: true }
    }

    const iconOrden = (campo) => {
      if (orden.value.campo !== campo) return '⇅'
      return orden.value.asc ? '↑' : '↓'
    }

    const formatBytes = (bytes) => {
      if (!bytes) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const limpiarFiltros = () => {
      filtros.value = { nombre: '', tipo: '', csvId: '' }
      paginaActual.value = 1
    }

    const exportarCSV = () => {
      const cab   = ['ID Planilla', 'Nombre', 'Ruta', 'Tamaño', 'Tipo']
      const filas = datosOrdenados.value.map(r => [r.csvIdPlanilla, r.nombre, r.ruta, r.tamano, r.tipo])
      const csv   = [cab, ...filas].map(f => f.map(v => `"${v ?? ''}"`).join(';')).join('\n')
      const blob  = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const url   = URL.createObjectURL(blob)
      const a     = document.createElement('a')
      a.href = url
      a.download = `archivos_sin_planilla_tramite.csv`
      a.click()
    }

    return {
      filtros, paginaActual, orden, tiposUnicos,
      datosFiltrados, datosPaginados, totalPaginas,
      pesoTotal, ordenarPor, iconOrden, formatBytes,
      limpiarFiltros, exportarCSV
    }
  }
}
</script>

<style scoped>
.th-sort { cursor: pointer; user-select: none; }
.th-sort:hover { background-color: #495057 !important; }
.table-sm td, .table-sm th { padding: 0.5rem; }
</style>